import type {
  AIProviderId,
  GeneratorType,
  GeneratorInput,
  GeneratorOutput,
} from '@creanizer/types'
import { useSettingsStore } from '../stores/settingsStore'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787'

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public isRateLimit: boolean = false
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export class ApiClient {
  private baseUrl: string

  constructor(baseUrl = API_URL) {
    this.baseUrl = baseUrl
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
    providerOverride?: AIProviderId
  ): Promise<T> {
    const settingsStore = useSettingsStore.getState()
    const provider = providerOverride ?? settingsStore.selectedProvider
    const activeKey = settingsStore.getActiveApiKeyForProvider(provider)
    
    if (!activeKey) {
      throw new ApiError('APIキーが設定されていません')
    }

    const url = `${this.baseUrl}${path}`
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-API-Key': activeKey.key,
      ...options.headers,
    }

    // 使用回数をインクリメント
    await settingsStore.incrementUsage(activeKey.id)

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      })

      // レート制限チェック
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After')
        const minutes = retryAfter ? parseInt(retryAfter) : 15
        const until = new Date(Date.now() + minutes * 60000)
        
        // 現在のキーをレート制限済みとしてマーク
        await settingsStore.setRateLimited(activeKey.id, until)
        
        throw new ApiError(
          `レート制限に達しました。${minutes}分後に再試行してください。`,
          429,
          true
        )
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          error: 'Network error',
          message: response.statusText,
        }))
        console.error('API Error:', {
          status: response.status,
          statusText: response.statusText,
          error: error,
          url: `${this.baseUrl}${path}`,
        })
        throw new ApiError(
          error.message || error.error || 'API request failed',
          response.status
        )
      }

      return response.json()
    } catch (error) {
      // ネットワークエラーやその他のエラー
      if (error instanceof ApiError) {
        throw error
      }
      
      throw new ApiError(
        error instanceof Error ? error.message : 'Unknown error occurred'
      )
    }
  }

  async generate(type: GeneratorType, params: Record<string, unknown>) {
    return this.request<{
      type: GeneratorType
      result: unknown
      modelUsed?: string
      timestamp: string
    }>(`/api/v1/generate/${type}`, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async getProjects() {
    return this.request<Array<{
      id: string
      name: string
      description?: string
      createdAt: string
      updatedAt: string
    }>>('/api/v1/projects')
  }

  async getProject(id: string) {
    return this.request<{
      id: string
      name: string
      description?: string
      metadata?: Record<string, unknown>
      createdAt: string
      updatedAt: string
    }>(`/api/v1/projects/${id}`)
  }

  async createProject(data: {
    name: string
    description?: string
    metadata?: Record<string, unknown>
  }) {
    return this.request<{
      id: string
      name: string
      description?: string
      createdAt: string
      updatedAt: string
    }>('/api/v1/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }
  
  async generateDungeonMap(params: Record<string, unknown>) {
    return this.request<{
      type: string
      result: any
      timestamp: string
    }>('/api/v1/generate/dungeon-map', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }
  
  // 相談ツール用のメソッド
  async consultationChat(params: any) {
    return this.request<{
      success: boolean
      data: {
        message: {
          id: string
          role: 'assistant'
          content: string
          timestamp: string
          metadata?: {
            personaId?: string
            personaName?: string
            confidence?: number
          }
        }
        sessionId: string
        metadata?: {
          modelUsed?: string
          tokenCount?: number
        }
      }
    }>('/api/v1/consultation/chat', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }
  
  async analyzePersona(characterData: any) {
    const modelId = useSettingsStore.getState().selectedModelId
    return this.request<{
      success: boolean
      data: {
        personalityStrength: number
        formality: number
        creativity: number
        empathy: number
        subjectivity: number
        reasoning: {
          personalityStrength: string
          formality: string
          creativity: string
          empathy: string
          subjectivity: string
        }
      }
    }>('/api/v1/consultation/analyze-persona', {
      method: 'POST',
      body: JSON.stringify({ characterData, modelId }),
    })
  }
  
  async consultationStream(params: any) {
    const settingsStore = useSettingsStore.getState()
    const activeKey = settingsStore.getActiveApiKeyForProvider(
      settingsStore.selectedProvider
    )
    
    if (!activeKey) {
      throw new ApiError('APIキーが設定されていません')
    }
    
    const url = `${this.baseUrl}/api/v1/consultation/stream`
    
    // 使用回数をインクリメント
    await settingsStore.incrementUsage(activeKey.id)
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': activeKey.key,
      },
      body: JSON.stringify(params),
    })
    
    if (!response.ok) {
      // レート制限チェック
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After')
        const minutes = retryAfter ? parseInt(retryAfter) : 15
        const until = new Date(Date.now() + minutes * 60000)
        
        // 現在のキーをレート制限済みとしてマーク
        await settingsStore.setRateLimited(activeKey.id, until)
        
        throw new ApiError(
          `レート制限に達しました。${minutes}分後に再試行してください。`,
          429,
          true
        )
      }
      
      const error = await response.json().catch(() => ({
        error: 'Network error',
        message: response.statusText,
      }))
      throw new ApiError(
        error.message || error.error || 'API request failed',
        response.status
      )
    }
    
    return response // ストリーミングレスポンスを返す
  }
  
  async compressHistory(params: {
    messages: any[]
    keepRecentCount?: number
    personaSettings?: any
    conferenceSettings?: any
  }) {
    const response = await this.request<{
      success: boolean
      data?: {
        compressedSummary: string
        originalMessageCount: number
        compressedMessageCount: number
      }
      error?: string
      message?: string
      debug?: {
        message: string
        type: string
      }
    }>('/api/v1/consultation/compress-history', {
      method: 'POST',
      body: JSON.stringify({
        ...params,
        modelId: useSettingsStore.getState().selectedModelId,
      }),
    })
    
    // compress-historyだけは{ success, data }形式なのでdataを取り出す
    if (response.success && response.data) {
      return response.data
    }
    
    // エラーレスポンスから詳細なメッセージを取得
    const errorMessage = response.message || response.error || '履歴圧縮に失敗しました'
    const debugInfo = response.debug ? `\n詳細: ${response.debug.message}` : ''
    
    throw new Error(errorMessage + debugInfo)
  }
  
  async generateSummary(params: {
    messages: any[]
    personaSettings?: any
    conferenceSettings?: any
  }) {
    const response = await this.request<{
      success: boolean
      data?: {
        summary: string
        messageCount: number
        timeRange: {
          start: string
          end: string
        }
      }
      error?: string
      message?: string
    }>('/api/v1/consultation/generate-summary', {
      method: 'POST',
      body: JSON.stringify({
        ...params,
        modelId: useSettingsStore.getState().selectedModelId,
      }),
    })
    
    // generate-summaryも{ success, data }形式なのでdataを取り出す
    if (response.success && response.data) {
      return response.data
    }
    
    // エラーレスポンスから詳細なメッセージを取得
    const errorMessage = response.message || response.error || 'まとめの生成に失敗しました'
    
    throw new Error(errorMessage)
  }
  
  // 対話シミュレータ用のメソッド
  async dialogueGenerate(params: any) {
    return this.request<{
      type: string
      result: any
      timestamp: string
    }>('/api/v1/generate/dialogue', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }
  
  async dialogueContinue(params: any) {
    return this.request<{
      type: string
      result: any
      timestamp: string
    }>('/api/v1/generate/dialogue/continue', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }
  
  async dialogueCompress(params: any) {
    return this.request<{
      type: string
      result: any
      timestamp: string
    }>('/api/v1/generate/dialogue/compress', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }
  
  async dialogueAnalyzeRelationship(params: any) {
    return this.request<{
      type: string
      result: any
      timestamp: string
    }>('/api/v1/generate/dialogue/analyze-relationship', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  // プロンプト翻訳
  async translatePrompt(params: {
    positivePrompt: string
    negativePrompt: string
    targetModel: string
  }) {
    return this.request<{
      type: string
      result: {
        translatedPositivePrompt: string
        translatedNegativePrompt: string
      }
      timestamp: string
    }>('/api/v1/generate/translate', {
      method: 'POST',
      body: JSON.stringify({
        ...params,
        modelId: useSettingsStore.getState().selectedModelId,
      }),
    })
  }

  // 画像生成（nanobanana pro）
  async generateImage(params: {
    prompt: string
    negativePrompt?: string
    mode: 'text_to_image' | 'image_edit'
    referenceImages?: string[]
    maskImage?: string  // マスク画像（白い部分が編集対象）
    insertImage?: string  // 挿入画像（マスク部分に合成する画像）
    aspectRatio: '9:16' | '16:9' | '1:1' | '4:3' | '3:4'
    imageSize: '1K' | '2K' | '4K'
  }) {
    return this.request<{
      type: string
      result: {
        imageData: string
        mimeType: string
        textResponse?: string
        metadata: {
          model: string
          aspectRatio: string
          imageSize: string
          mode: string
          timestamp: string
        }
      }
      timestamp: string
    }>('/api/v1/generate/image-generate', {
      method: 'POST',
      body: JSON.stringify(params),
    }, 'gemini')
  }
}

export const apiClient = new ApiClient()
